import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    FiHeart,
    FiShoppingCart,
    FiLoader,
    FiGift,
    FiArrowLeft,
    FiShield,
    FiTruck,
    FiCheckCircle,
    FiPhoneCall,
    FiMaximize2,
    FiChevronDown,
    FiChevronUp,
    FiAward,
    FiCheck,
    FiMapPin,
    FiZap,
    FiCpu,
    FiSliders,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import productService from '../services/productService';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import { useShop } from '../context/ShopContext';
import { useLightbox } from '../context/LightboxContext';
import { useDiscount } from '../context/DiscountContext';
import { getImageUrl } from '../utils/imageUrl';
import {
    getProductInstallmentPlans,
    calculateProductPlan,
} from '../utils/installmentCalculator';
import {
    cashPriceFromMrp,
    getProductDiscount,
    formatPriceDisplay,
} from '../utils/cashPricing';
import {
    BUSINESS_WHATSAPP_CHAT_URL,
    BUSINESS_PHONE_MAIN,
    BUSINESS_ADDRESS_SINGLE,
    BUSINESS_HOURS,
    BUSINESS_MAP_URL,
} from '../config/businessInfo';
import ProductCard from '../components/common/ProductCard';
import QuickBuyModal from '../components/cart/QuickBuyModal';
import SuccessModal from '../components/common/SuccessModal';
import ShowroomMapSection from '../components/common/ShowroomMapSection';
import { REAL_SHOP_IMAGES } from '../config/realMedia';
import './ProductDetailPage.css';

const SHOWROOM_GALLERY_SHOTS = [
    {
        src: REAL_SHOP_IMAGES.refrigeratorAisle,
        label: 'Showroom Floor Display',
    },
    {
        src: REAL_SHOP_IMAGES.storefront,
        label: 'Lahore Flagship Store',
    },
    {
        src: '/assets/media/images/products/IMG_4508.HEIC_20261022224947.jpg',
        label: 'Brand Warranty Stock',
    },
];


const getDynamicProductCards = (cat, prodName, brandName) => {
    const s = `${cat || ''} ${prodName || ''}`.toLowerCase();
    if (s.includes('air') || s.includes('ac') || s.includes('inverter') || s.includes('split') || s.includes('cooling')) {
        return [
            { icon: <FiZap />, title: 'Inverter Energy Saving', desc: 'High-efficiency inverter compressor, reducing electricity bills by up to 60% with rapid chill.' },
            { icon: <FiShield />, title: 'Official Compressor Warranty', desc: `Covered under official ${brandName || 'authorized'} warranty card with certified parts protection.` },
            { icon: <FiCpu />, title: 'Low-Voltage Startup (150V)', desc: 'Engineered for smooth continuous cooling without needing an external stabilizer.' },
            { icon: <FiCheckCircle />, title: '100% Pure Copper Condenser', desc: 'High-grade copper condenser tube with anti-rust gold/blue fin long-term coating.' }
        ];
    }
    if (s.includes('fridge') || s.includes('refrigerator') || s.includes('freezer') || s.includes('deep')) {
        return [
            { icon: <FiZap />, title: 'Hybrid Inverter Cooling', desc: 'Smart sensor multi-air flow maintaining freezing temperatures even during load shedding.' },
            { icon: <FiShield />, title: 'Official 10-Year Warranty', desc: `Official ${brandName || 'Manufacturer'} warranty card claimable across all authorized service centers.` },
            { icon: <FiCpu />, title: 'Low-Voltage Performance', desc: 'Smooth operation on voltages as low as 135V with high-density thermal insulation.' },
            { icon: <FiCheckCircle />, title: 'Food Care & Freshness', desc: 'Anti-bacterial deodorizing filter keeping fresh vegetables and dairy crisp for days.' }
        ];
    }
    if (s.includes('tv') || s.includes('led') || s.includes('oled') || s.includes('qled') || s.includes('smart')) {
        return [
            { icon: <FiZap />, title: 'Ultra-Clear Vivid Display', desc: 'High dynamic color contrast, deep blacks, and wide 178-degree panoramic viewing angles.' },
            { icon: <FiShield />, title: 'Official Panel Warranty', desc: `Authentic warranty coverage provided directly by authorized ${brandName || 'brand'} service network.` },
            { icon: <FiCpu />, title: 'Smart OS & Apps Built-in', desc: 'Preloaded YouTube, Netflix, Prime Video, with Google Chromecast and voice remote.' },
            { icon: <FiCheckCircle />, title: 'Cinematic Surround Audio', desc: 'Dual high-output stereo speakers with Dolby Digital audio clarity enhancement.' }
        ];
    }
    if (s.includes('wash') || s.includes('spin') || s.includes('dryer') || s.includes('laundry')) {
        return [
            { icon: <FiZap />, title: 'High-Torque Power Wash', desc: 'Deep stain removal and powerful pulsating wave action while protecting fabric fibers.' },
            { icon: <FiShield />, title: 'Official Motor Warranty', desc: `Backed by ${brandName || 'manufacturer'} certified motor warranty and dedicated technician support.` },
            { icon: <FiCpu />, title: 'Smart Water & Energy Sensor', desc: 'Automatic load sensing ensuring low electricity and water consumption per wash.' },
            { icon: <FiCheckCircle />, title: 'Rust-Proof Poly Body', desc: 'Durable anti-corrosive cabinet designed to withstand humid bathroom conditions.' }
        ];
    }
    return [
        { icon: <FiZap />, title: 'High Energy Efficiency', desc: 'Engineered for economical power usage and reliable everyday performance.' },
        { icon: <FiShield />, title: 'Official Brand Warranty', desc: `Genuine official warranty card included in sealed box, valid across all Pakistan.` },
        { icon: <FiCpu />, title: '100% Brand Sealed Box', desc: 'Original factory packaging with intact serial barcode security seals.' },
        { icon: <FiCheckCircle />, title: 'Showroom Tested & Inspected', desc: 'Thorough quality check by Lahore Showroom dispatch team prior to release.' }
    ];
};

const ProductDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isInWishlist, toggleWishlist, addToCart } = useShop();
    const { openLightbox } = useLightbox();
    const { effectiveDiscount } = useDiscount();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState('');

    const [activeImage, setActiveImage] = useState(null);
    const [activeTab, setActiveTab] = useState('specs');
    const [selectedDownPayment, setSelectedDownPayment] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [showTermsAccordion, setShowTermsAccordion] = useState(false);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [selectedPurchaseMode, setSelectedPurchaseMode] = useState('cash'); // 'cash' | 'four_month' | 'installment'
    const [isQuickBuyOpen, setIsQuickBuyOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [placedOrderSummary, setPlacedOrderSummary] = useState(null);

    const handleOrderPlaced = (orderSummary) => {
        setPlacedOrderSummary(orderSummary);
        setIsSuccessModalOpen(true);
    };

    const loadRelatedProducts = useCallback((prodData) => {
        const catId = typeof prodData.category === 'object' ? prodData.category?._id : prodData.category;
        productService
            .getAll(1, 4, '', catId || '')
            .then((res) => {
                if (res?.products) {
                    setRelatedProducts(res.products.filter((p) => p._id !== prodData._id).slice(0, 4));
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!id) {
                setNotFound(true);
                setLoading(false);
                return;
            }
            setLoading(true);
            setNotFound(false);
            setError('');
            setProduct(null);

            try {
                const data = await productService.getById(id);
                if (!cancelled) {
                    if (data) {
                        setProduct(data);
                        setActiveImage(getImageUrl(data.image));
                        loadRelatedProducts(data);
                    } else {
                        setNotFound(true);
                    }
                }
            } catch (err) {
                if (cancelled) return;
                if (err.status === 404) {
                    setNotFound(true);
                } else {
                    setError(
                        err.message ||
                            'Unable to load this product. Please try again.'
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [id, loadRelatedProducts]);

    // Silent background re-fetch when admin adds, updates, or removes product installment plans or prices
    const refreshProductSilently = useCallback(async () => {
        if (!id) return;
        try {
            const freshData = await productService.getById(id);
            if (freshData) {
                setProduct((prev) => {
                    const prevMainImg = prev ? getImageUrl(prev.image) : null;
                    const nextMainImg = getImageUrl(freshData.image);
                    setActiveImage((currImg) =>
                        !currImg || currImg === prevMainImg ? nextMainImg : currImg
                    );
                    return freshData;
                });
                setNotFound(false);
                setError('');
                loadRelatedProducts(freshData);
            }
        } catch (err) {
            if (err?.status === 404) {
                setNotFound(true);
            }
        }
    }, [id, loadRelatedProducts]);

    useRealtimeSync('products', refreshProductSilently);
    useRealtimeSync('prices', refreshProductSilently);
    useRealtimeSync('categories', refreshProductSilently);

    // Strictly dynamic installment plans configured on this product via Admin Dashboard
    const planTiers = useMemo(
        () => getProductInstallmentPlans(product),
        [product]
    );

    const hasInstallmentPlans = useMemo(
        () =>
            Boolean(
                product?.installment_product !== false &&
                    planTiers.length > 0 &&
                    Number(product?.mrp) > 0
            ),
        [product?.installment_product, product?.mrp, planTiers]
    );

    const hasFourMonthOffer = useMemo(
        () => Boolean(product?.four_month_mrp_offer && Number(product?.mrp) > 0),
        [product?.four_month_mrp_offer, product?.mrp]
    );

    const availableDownPayments = useMemo(() => {
        if (!hasInstallmentPlans) return [];
        const unique = [...new Set(planTiers.map((t) => t.down_payment_percent))];
        return unique.sort((a, b) => a - b);
    }, [hasInstallmentPlans, planTiers]);

    const allConfiguredDurations = useMemo(() => {
        if (!hasInstallmentPlans) return [];
        const unique = [...new Set(planTiers.map((t) => t.months))];
        return unique.sort((a, b) => a - b);
    }, [hasInstallmentPlans, planTiers]);

    const effectiveDownPayment = useMemo(() => {
        if (availableDownPayments.length === 0) return null;
        return availableDownPayments.includes(selectedDownPayment)
            ? selectedDownPayment
            : availableDownPayments[0];
    }, [availableDownPayments, selectedDownPayment]);

    const availableDurations = useMemo(() => {
        if (!hasInstallmentPlans || effectiveDownPayment === null) return [];
        const matching = planTiers.filter(
            (t) => t.down_payment_percent === effectiveDownPayment
        );
        const source = matching.length > 0 ? matching : planTiers;
        const unique = [...new Set(source.map((t) => t.months))];
        return unique.sort((a, b) => a - b);
    }, [hasInstallmentPlans, planTiers, effectiveDownPayment]);

    const effectiveDuration = useMemo(() => {
        if (availableDurations.length === 0) return null;
        return availableDurations.includes(selectedDuration)
            ? selectedDuration
            : availableDurations[availableDurations.length - 1];
    }, [availableDurations, selectedDuration]);

    useEffect(() => {
        if (effectiveDownPayment !== null && selectedDownPayment !== effectiveDownPayment) {
            setSelectedDownPayment(effectiveDownPayment);
        }
    }, [effectiveDownPayment, selectedDownPayment]);

    useEffect(() => {
        if (effectiveDuration !== null && selectedDuration !== effectiveDuration) {
            setSelectedDuration(effectiveDuration);
        }
    }, [effectiveDuration, selectedDuration]);

    useEffect(() => {
        if (!hasInstallmentPlans && selectedPurchaseMode === 'installment') {
            setSelectedPurchaseMode('cash');
        }
        if (!hasFourMonthOffer && selectedPurchaseMode === 'four_month') {
            setSelectedPurchaseMode('cash');
        }
        if (!hasInstallmentPlans && activeTab === 'calculator') {
            setActiveTab('specs');
        }
    }, [hasInstallmentPlans, hasFourMonthOffer, selectedPurchaseMode, activeTab]);

    const selectedPlanTier = useMemo(() => {
        if (!hasInstallmentPlans || effectiveDownPayment === null || effectiveDuration === null) {
            return null;
        }
        return (
            planTiers.find(
                (t) =>
                    t.down_payment_percent === effectiveDownPayment &&
                    t.months === effectiveDuration
            ) ||
            planTiers[0] ||
            null
        );
    }, [hasInstallmentPlans, planTiers, effectiveDownPayment, effectiveDuration]);

    const installmentCalc = useMemo(() => {
        if (!hasInstallmentPlans || !selectedPlanTier) {
            return null;
        }
        return calculateProductPlan(product, selectedPlanTier);
    }, [hasInstallmentPlans, product, selectedPlanTier]);

    const productDiscount = useMemo(
        () => getProductDiscount(product, effectiveDiscount),
        [product, effectiveDiscount]
    );

    const cashUnitPrice = useMemo(
        () => cashPriceFromMrp(product?.mrp, productDiscount),
        [product?.mrp, productDiscount]
    );

    const cashSavings = useMemo(() => {
        const mrpNum = Number(product?.mrp || 0);
        return Math.max(0, mrpNum - cashUnitPrice);
    }, [product?.mrp, cashUnitPrice]);

    if (loading) {
        return (
            <div
                className='container'
                style={{
                    paddingTop: '120px',
                    paddingBottom: '80px',
                    textAlign: 'center',
                }}
            >
                <FiLoader
                    style={{
                        fontSize: '2.5rem',
                        animation: 'spin 0.9s linear infinite',
                    }}
                />
                <p style={{ marginTop: '1rem', color: 'var(--gray-600)' }}>
                    Loading appliance details…
                </p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (notFound) {
        return (
            <div
                className='container'
                style={{ paddingTop: '100px', textAlign: 'center' }}
            >
                <h1>Product not found</h1>
                <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
                    This product may have been removed or is no longer available.
                </p>
                <Link to='/products' className='btn btn-primary'>
                    Back to Products
                </Link>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className='container'
                style={{ paddingTop: '100px', textAlign: 'center' }}
            >
                <h1>Something went wrong</h1>
                <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
                    {error}
                </p>
                <Link to='/products' className='btn btn-primary'>
                    Back to Products
                </Link>
            </div>
        );
    }

    if (!product) return null;

    const baseImageSrc = getImageUrl(product.image);
    const displayedImage = activeImage || baseImageSrc;
    const savedToWishlist = isInWishlist(product._id);

    const handleWishlistClick = () => {
        toggleWishlist(product);
        toast.success(
            savedToWishlist ? 'Removed from wishlist' : 'Saved to wishlist'
        );
    };

    const handleAddToCart = () => {
        addToCart(product, quantity);
        const discountText = productDiscount > 0 ? ` (${productDiscount}% off MRP)` : '';
        toast.success(
            quantity > 1
                ? `${quantity} items added to cart at cash price${discountText}`
                : `Added to cart at cash price${discountText}`
        );
    };

    const categoryName =
        typeof product.category === 'object' && product.category?.name
            ? product.category.name
            : null;
    const featureList = Array.isArray(product.features) ? product.features : [];
    const brand = product.brand || 'Rahber Electronics';

    const dynamicCards = getDynamicProductCards(
        categoryName,
        product.name,
        brand
    );

    return (
        <div className='product-detail-page'>
            <div className='container'>
                {/* Navigation Back Button + Breadcrumb */}
                <div className='product-detail-nav-row'>
                    <button
                        type='button'
                        onClick={() => navigate(-1)}
                        className='product-page-back-btn'
                        aria-label='Go back to previous page'
                    >
                        <FiArrowLeft /> Back
                    </button>
                    <div className='breadcrumb'>
                        <Link to='/'>Home</Link> / <Link to='/products'>Products</Link>
                        {categoryName ? ` / ${categoryName}` : ''} / <span>{product.name}</span>
                    </div>
                </div>

                {/* Main Product Showcase Grid */}
                <div className='product-detail-grid'>
                    {/* Left Column: Interactive Multi-View Gallery + Showroom Trust Strip */}
                    <div className='product-gallery-col'>
                        <div className='product-gallery-card'>
                            <div className='product-gallery-hero-wrap'>
                                <div
                                    className='product-main-image-wrap zoomable-image'
                                    onClick={() =>
                                        openLightbox(
                                            displayedImage,
                                            product.name,
                                            `${brand || 'Official'} • Genuine Box-Packed with Official Warranty`
                                        )
                                    }
                                    title='Click to enlarge high-resolution view'
                                    role='button'
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            openLightbox(
                                                displayedImage,
                                                product.name,
                                                `${brand || 'Official'} • Genuine Box-Packed with Official Warranty`
                                            );
                                        }
                                    }}
                                >
                                    <img
                                        src={displayedImage}
                                        alt={product.name}
                                        className='main-image'
                                    />
                                    <div className='product-zoom-hint'>
                                        <FiMaximize2 />
                                        <span>Click to Zoom</span>
                                    </div>
                                    <div className='product-brand-floating-seal'>
                                        <FiAward /> {brand}
                                    </div>
                                </div>
                            </div>

                            {/* Gallery Thumbnails */}
                            <div className='product-thumbnails-strip'>
                                <button
                                    type='button'
                                    className={`product-thumb-btn${displayedImage === baseImageSrc ? ' product-thumb-btn--active' : ''}`}
                                    onClick={() => setActiveImage(baseImageSrc)}
                                    title='Main product view'
                                >
                                    <img src={baseImageSrc} alt='Main view' />
                                    <span>Main View</span>
                                </button>
                                {SHOWROOM_GALLERY_SHOTS.map((shot, idx) => (
                                    <button
                                        key={idx}
                                        type='button'
                                        className={`product-thumb-btn${displayedImage === shot.src ? ' product-thumb-btn--active' : ''}`}
                                        onClick={() => setActiveImage(shot.src)}
                                        title={shot.label}
                                    >
                                        <img src={shot.src} alt={shot.label} />
                                        <span>{shot.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* High-Trust Showroom Verification Strip */}
                        <div className='product-trust-strip'>
                            <div className='product-trust-item'>
                                <FiShield className='product-trust-icon' />
                                <div>
                                    <strong>Original Box-Packed</strong>
                                    <span>Sealed box with serial barcode inspection</span>
                                </div>
                            </div>
                            <div className='product-trust-item'>
                                <FiAward className='product-trust-icon' />
                                <div>
                                    <strong>Brand Warranty Card</strong>
                                    <span>Manufacturer warranty coverage included</span>
                                </div>
                            </div>
                            <div className='product-trust-item'>
                                <FiTruck className='product-trust-icon' />
                                <div>
                                    <strong>Lahore Showroom Delivery</strong>
                                    <span>Handled with care by trained showroom staff</span>
                                </div>
                            </div>
                            <div className='product-trust-item'>
                                <FiPhoneCall className='product-trust-icon' />
                                <div>
                                    <strong>Call Verification on Every Order</strong>
                                    <span>Specialist confirms order &amp; plan before dispatch</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Pricing, Dual Action Purchase, Specs Overview */}
                    <div className='product-details'>
                        <div className='product-meta-header'>
                            <span className='product-brand-tag'>
                                <FiAward aria-hidden='true' /> {brand}
                            </span>
                            <div className='product-stock-badge'>
                                <span className='product-stock-dot' />
                                <span>In Stock at Lahore Showroom</span>
                            </div>
                        </div>

                        {product.sku && (
                            <p className='product-sku-line'>
                                SKU: <code>{product.sku}</code>
                            </p>
                        )}

                        <h1 className='product-title'>{product.name}</h1>

                        {/* Dynamic Interactive Buying Mode Selector */}
                        <div className='price-section'>
                            <div className='purchase-mode-label-bar'>
                                <span className='purchase-mode-label'>
                                    {hasFourMonthOffer || (hasInstallmentPlans && installmentCalc?.ok)
                                        ? 'Select Your Preferred Buying Option:'
                                        : 'Official Showroom Cash Price:'}
                                </span>
                                {(hasFourMonthOffer || (hasInstallmentPlans && installmentCalc?.ok)) && (
                                    <span className='purchase-mode-sublabel'>
                                        Click any option below to choose terms:
                                    </span>
                                )}
                            </div>

                            <div className='price-dual-cards price-cards--3col'>
                                {/* Option 1: Direct Cash Purchase */}
                                <div
                                    className={`price-card price-card--cash ${selectedPurchaseMode === 'cash' ? 'price-card--selected' : ''}`}
                                    onClick={() => setSelectedPurchaseMode('cash')}
                                    role='button'
                                    tabIndex={0}
                                    title={productDiscount > 0 ? `Click to buy on cash with ${productDiscount}% instant discount` : 'Click to buy on cash'}
                                >
                                    <div className='price-card-header'>
                                        <span className='price-card-title'>
                                            {hasFourMonthOffer || (hasInstallmentPlans && installmentCalc?.ok)
                                                ? '1. Direct Cash Buy'
                                                : 'Direct Cash Buy'}
                                        </span>
                                        {productDiscount > 0 && cashSavings > 0 ? (
                                            <span className='price-savings-pill'>
                                                Instant Discount (Save {formatPriceDisplay(cashSavings)})
                                            </span>
                                        ) : (
                                            <span className='price-savings-pill'>
                                                Official Cash Price
                                            </span>
                                        )}
                                    </div>
                                    <div className='price-card-body'>
                                        <div className='price-value price-value--cash-main'>
                                            {formatPriceDisplay(cashUnitPrice)}
                                        </div>
                                        {productDiscount > 0 && cashSavings > 0 && (
                                            <div className='price-item--mrp-reference'>
                                                Official List MRP: <span className='price-mrp-strike'>{formatPriceDisplay(product.mrp)}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className='price-card-footer'>
                                        <FiCheck className='price-check-icon' />
                                        <span>
                                            {productDiscount > 0
                                                ? `Instant ${productDiscount}% cash discount automatically applied`
                                                : 'Direct cash purchase at official list MRP'}
                                        </span>
                                    </div>
                                </div>

                                {/* Option 2: Special 0% Markup (4 Months) */}
                                {hasFourMonthOffer && (
                                    <div
                                        className={`price-card price-card--four-month ${selectedPurchaseMode === 'four_month' ? 'price-card--selected' : ''}`}
                                        onClick={() => setSelectedPurchaseMode('four_month')}
                                        role='button'
                                        tabIndex={0}
                                        title='Click to buy on 0% Markup 4-Month Plan'
                                    >
                                        <div className='price-card-header'>
                                            <span className='price-card-title'>2. 0% Markup Offer</span>
                                            <span className='price-plan-badge price-plan-badge--zero'>0% Interest</span>
                                        </div>
                                        <div className='price-card-body'>
                                            <div className='price-value price-value--four-month-main'>
                                                {formatPriceDisplay(Math.round(Number(product.mrp) / 4))}
                                                <span className='price-per-month'>/month</span>
                                            </div>
                                            <div className='price-item--mrp-reference'>
                                                1 advance + 3 equal payments at listed MRP
                                            </div>
                                        </div>
                                        <div className='price-card-footer price-card-footer--four-month'>
                                            <FiGift className='price-check-icon' style={{ color: '#D97706' }} />
                                            <span>Zero profit, zero markup at listed MRP</span>
                                        </div>
                                    </div>
                                )}

                                {/* Option 3: Dynamic Admin-Configured Easy Installment Plan */}
                                {hasInstallmentPlans && installmentCalc?.ok && (
                                    <div
                                        className={`price-card price-card--installment ${selectedPurchaseMode === 'installment' ? 'price-card--selected' : ''}`}
                                        onClick={() => setSelectedPurchaseMode('installment')}
                                        role='button'
                                        tabIndex={0}
                                        title='Click to buy on easy monthly installments'
                                    >
                                        <div className='price-card-header'>
                                            <span className='price-card-title'>
                                                {hasFourMonthOffer ? '3. Easy Installments' : '2. Easy Installments'}
                                            </span>
                                            <span className='price-plan-badge'>
                                                {allConfiguredDurations.join(' / ')}{' '}
                                                {allConfiguredDurations.length === 1 && allConfiguredDurations[0] === 1
                                                    ? 'Month'
                                                    : 'Months'}
                                            </span>
                                        </div>
                                        <div className='price-card-body'>
                                            <div className='price-value price-value--installment-main'>
                                                {formatPriceDisplay(installmentCalc.monthlyInstallment)}
                                                <span className='price-per-month'>/month</span>
                                            </div>
                                            <div className='price-item--mrp-reference'>
                                                {`${installmentCalc.planMonths} ${installmentCalc.planMonths === 1 ? 'Month' : 'Months'} • ${installmentCalc.downPaymentPercent}% Down`}
                                            </div>
                                        </div>
                                        <div className='price-card-footer price-card-footer--installment'>
                                            <FiSliders className='price-check-icon' style={{ color: '#0B2A67' }} />
                                            <span>Fast 24 to 48 hours phone verification</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Dynamic Plan Tiers Subselector when Installment Mode is Selected */}
                            {selectedPurchaseMode === 'installment' && hasInstallmentPlans && installmentCalc?.ok && (
                                <div className='installment-inline-tier-selector'>
                                    {/* Select Downpayment */}
                                    <div className='installment-selector-group'>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
                                            <span className='installment-group-title' style={{ marginBottom: 0 }}>Select Downpayment</span>
                                        </div>
                                        <div className='installment-cards-row'>
                                            {availableDownPayments.map((dp) => {
                                                const isSelected = effectiveDownPayment === dp;
                                                return (
                                                    <button
                                                        key={dp}
                                                        type='button'
                                                        className={`installment-card-btn ${isSelected ? 'installment-card-btn--selected' : ''}`}
                                                        onClick={() => setSelectedDownPayment(dp)}
                                                        aria-pressed={isSelected}
                                                    >
                                                        <span className={`installment-radio ${isSelected ? 'installment-radio--selected' : ''}`}>
                                                            {isSelected && <span className='installment-radio-dot' />}
                                                        </span>
                                                        <span className='installment-card-label'>{dp}%</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Installment Duration */}
                                    <div className='installment-selector-group'>
                                        <span className='installment-group-title'>Installment Duration</span>
                                        <div className='installment-cards-row'>
                                            {availableDurations.map((m) => {
                                                const isSelected = effectiveDuration === m;
                                                const tierForOption = planTiers.find(
                                                    (t) =>
                                                        t.down_payment_percent === effectiveDownPayment &&
                                                        t.months === m
                                                );
                                                const isZeroMarkup = tierForOption?.profit_percentage === 0;
                                                return (
                                                    <button
                                                        key={m}
                                                        type='button'
                                                        className={`installment-card-btn ${isSelected ? 'installment-card-btn--selected' : ''} ${isZeroMarkup && !isSelected ? 'installment-card-btn--zero-markup' : ''}`}
                                                        onClick={() => setSelectedDuration(m)}
                                                        aria-pressed={isSelected}
                                                    >
                                                        <span className={`installment-radio ${isSelected ? 'installment-radio--selected' : ''}`}>
                                                            {isSelected && <span className='installment-radio-dot' />}
                                                        </span>
                                                        <span className='installment-card-label'>
                                                            {m} {m === 1 ? 'Month' : 'Months'}
                                                            {isZeroMarkup && (
                                                                <span className='installment-zero-markup-badge'>0% Markup</span>
                                                            )}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className='tier-calc-mini-summary'>
                                        <div className='calc-mini-item'>
                                            <span>Required Down Payment ({installmentCalc.downPaymentPercent}%):</span>
                                            <strong>{formatPriceDisplay(installmentCalc.downPaymentAmount * quantity)}</strong>
                                        </div>
                                        <div className='calc-mini-item'>
                                            <span>Monthly Installment:</span>
                                            <strong style={{ color: 'var(--accent-orange, #EA580C)' }}>
                                                {formatPriceDisplay(installmentCalc.monthlyInstallment * quantity)} / month
                                            </strong>
                                            <small>
                                                ({installmentCalc.remainingMonths} monthly{' '}
                                                {installmentCalc.remainingMonths === 1 ? 'payment' : 'payments'})
                                            </small>
                                        </div>
                                        <div className='calc-mini-item'>
                                            <span>Total Payable:</span>
                                            <strong>{formatPriceDisplay(installmentCalc.totalPayable * quantity)}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {product.description && (
                            <p className='product-description'>{product.description}</p>
                        )}

                        {/* Quantity and Primary Action Buttons (Spacious, Zero Overlap, High Conversion) */}
                        <div className='purchase-options'>
                            <div className='purchase-options-stack'>
                                {/* Row 1: Quantity + Dynamic Buy Now Button matching selected option */}
                                <div className='purchase-primary-row'>
                                    <div className='quantity-selector'>
                                        <label>Qty:</label>
                                        <div className='quantity-controls'>
                                            <button
                                                type='button'
                                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                                aria-label='Decrease quantity'
                                            >
                                                −
                                            </button>
                                            <input type='number' value={quantity} readOnly />
                                            <button
                                                type='button'
                                                onClick={() => setQuantity(quantity + 1)}
                                                aria-label='Increase quantity'
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* 2x2 Equal Action Buttons Grid (Buy Now, Add to Cart, WhatsApp, Wishlist) */}
                                    <div className='product-actions-grid'>
                                        {/* 1. Buy Now */}
                                        <button
                                            type='button'
                                            className={`btn btn-action-equal btn-buy-now-equal ${
                                                selectedPurchaseMode === 'four_month'
                                                    ? 'btn-buy-now-equal--gold'
                                                    : selectedPurchaseMode === 'installment'
                                                    ? 'btn-buy-now-equal--blue'
                                                    : ''
                                            }`}
                                            onClick={() => setIsQuickBuyOpen(true)}
                                        >
                                            {selectedPurchaseMode === 'cash' && (
                                                <>
                                                    <FiZap className='btn-action-icon' />
                                                    <span>Buy Now ({formatPriceDisplay(cashUnitPrice * quantity)})</span>
                                                </>
                                            )}
                                            {selectedPurchaseMode === 'four_month' && (
                                                <>
                                                    <FiGift className='btn-action-icon' />
                                                    <span>Buy on 0% Markup</span>
                                                </>
                                            )}
                                            {selectedPurchaseMode === 'installment' && (
                                                <>
                                                    <FiSliders className='btn-action-icon' />
                                                    <span>Apply on Installment</span>
                                                </>
                                            )}
                                        </button>

                                        {/* 2. Add to Cart */}
                                        <button
                                            type='button'
                                            className='btn btn-action-equal btn-add-cart-outline'
                                            onClick={handleAddToCart}
                                        >
                                            <FiShoppingCart className='btn-action-icon' />
                                            <span>Add to Cart</span>
                                        </button>

                                        {/* 3. WhatsApp Order (Parrot Green) */}
                                        <a
                                            href={`${BUSINESS_WHATSAPP_CHAT_URL}?text=${encodeURIComponent(
                                                selectedPurchaseMode === 'installment' && installmentCalc?.ok
                                                    ? `Hello Rahber Corporation, I am interested in purchasing: ${product.name} (Qty: ${quantity}) on the ${installmentCalc.planMonths}-Month Installment Plan (${installmentCalc.downPaymentPercent}% Down: ${formatPriceDisplay(installmentCalc.downPaymentAmount * quantity)}, Monthly: ${formatPriceDisplay(installmentCalc.monthlyInstallment * quantity)}, Total: ${formatPriceDisplay(installmentCalc.totalPayable * quantity)}). Please confirm availability and verification steps.`
                                                    : selectedPurchaseMode === 'four_month' && hasFourMonthOffer
                                                    ? `Hello Rahber Corporation, I am interested in purchasing: ${product.name} (Qty: ${quantity}) on the 4-Month 0% Markup Offer (${formatPriceDisplay(Math.round(Number(product.mrp) / 4) * quantity)}/month). Please confirm stock and delivery.`
                                                    : `Hello Rahber Corporation, I am interested in purchasing: ${product.name} (Qty: ${quantity}, Cash Price: ${formatPriceDisplay(cashUnitPrice * quantity)}). Please confirm stock and delivery.`
                                            )}`}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='btn btn-action-equal btn-whatsapp-order'
                                            title='Order directly on WhatsApp'
                                        >
                                            <FaWhatsapp className='btn-action-icon' />
                                            <span>Order on WhatsApp</span>
                                        </a>

                                        {/* 4. Add to Wishlist (Equal Button Style) */}
                                        <button
                                            type='button'
                                            className={`btn btn-action-equal btn-wishlist-equal ${
                                                savedToWishlist ? 'btn-wishlist-equal--active' : ''
                                            }`}
                                            onClick={handleWishlistClick}
                                            title={
                                                savedToWishlist
                                                    ? 'Remove from wishlist'
                                                    : 'Add to wishlist'
                                            }
                                            aria-pressed={savedToWishlist}
                                        >
                                            <FiHeart
                                                className='btn-action-icon'
                                                style={
                                                    savedToWishlist
                                                        ? { fill: 'currentColor' }
                                                        : undefined
                                                }
                                            />
                                            <span>
                                                {savedToWishlist ? 'Saved in Wishlist' : 'Add to Wishlist'}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className='product-delivery-notice'>
                                <FiTruck className='notice-icon' />
                                <span>
                                    <strong>Showroom Delivery in Lahore:</strong> Same-day doorstep dispatch on orders confirmed before 3:00 PM.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Interactive Multi-Tab Deep Dive Section (Eliminating Boring Walls of Text) */}
                <section id='product-tabs-section' className='product-tabs-wrapper'>
                    <div className='product-tabs-nav'>
                        <button
                            type='button'
                            className={`product-tab-btn${activeTab === 'specs' ? ' product-tab-btn--active' : ''}`}
                            onClick={() => setActiveTab('specs')}
                        >
                            <FiCpu /> Specifications &amp; Highlights
                        </button>
                        {hasInstallmentPlans && (
                            <button
                                type='button'
                                className={`product-tab-btn${activeTab === 'calculator' ? ' product-tab-btn--active' : ''}`}
                                onClick={() => setActiveTab('calculator')}
                            >
                                <FiSliders /> Installment Process
                            </button>
                        )}
                        <button
                            type='button'
                            className={`product-tab-btn${activeTab === 'warranty' ? ' product-tab-btn--active' : ''}`}
                            onClick={() => setActiveTab('warranty')}
                        >
                            <FiShield /> Warranty &amp; Authenticity
                        </button>
                        <button
                            type='button'
                            className={`product-tab-btn${activeTab === 'showroom' ? ' product-tab-btn--active' : ''}`}
                            onClick={() => setActiveTab('showroom')}
                        >
                            <FiMapPin /> Showroom Pickup &amp; Location
                        </button>
                    </div>

                    <div className='tabs-scroll-hint-bar'>
                        <span className='tabs-scroll-pill'>👉 Slide / Scroll sideways to switch tabs &amp; specifications 👈</span>
                    </div>

                    <div className='product-tab-content-panel'>
                        {/* Tab 1: Specs & Visual Highlights */}
                        {activeTab === 'specs' && (
                            <div className='tab-specs-pane'>
                                <div className='specs-visual-feature-grid'>
                                    {dynamicCards.map((card, idx) => (
                                        <div key={idx} className='spec-feature-card'>
                                            <div className='spec-feature-icon-wrap'>
                                                {card.icon}
                                            </div>
                                            <div className='spec-feature-info'>
                                                <h4>{card.title}</h4>
                                                <p>{card.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {featureList.length > 0 && (
                                    <div className='specs-features-list-wrap'>
                                        <h3 className='specs-subheading'>Product Highlights &amp; Inclusions:</h3>
                                        <div className='specs-features-chips-grid'>
                                            {featureList.map((feature, idx) => (
                                                <div key={idx} className='specs-feature-chip'>
                                                    <FiCheck className='chip-check' />
                                                    <span>{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className='specs-table-wrap'>
                                    <h3 className='specs-subheading'>Technical Data Sheet:</h3>
                                    <table className='specs-table'>
                                        <tbody>
                                            <tr>
                                                <th>Brand</th>
                                                <td>{brand}</td>
                                            </tr>
                                            {product.model_number && (
                                                <tr>
                                                    <th>Model Number</th>
                                                    <td>{product.model_number}</td>
                                                </tr>
                                            )}
                                            {product.sku && (
                                                <tr>
                                                    <th>SKU Code</th>
                                                    <td><code>{product.sku}</code></td>
                                                </tr>
                                            )}
                                            {categoryName && (
                                                <tr>
                                                    <th>Category</th>
                                                    <td>{categoryName}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <th>Packaging Condition</th>
                                                <td>100% Brand New, Sealed Box with Serial Number</td>
                                            </tr>
                                            <tr>
                                                <th>Warranty Coverage</th>
                                                <td>Official Manufacturer Card Included in Packaging</td>
                                            </tr>
                                            <tr>
                                                <th>Voltage &amp; Frequency</th>
                                                <td>220V-240V / 50Hz (Pakistan Standard)</td>
                                            </tr>
                                            <tr>
                                                <th>Showroom Verification</th>
                                                <td>Inspected and verified at Main Bazar Chandni Chowk, Lahore</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Tab 2: Easy Installment Plan & Live Calculator */}
                        {activeTab === 'calculator' && hasInstallmentPlans && (
                            <div className='tab-installment-pane'>
                                <div className='installment-section-detail'>
                                    {/* Left: 4-Step Process Cards */}
                                    <div className='installment-rules-panel'>
                                        <div className='installment-panel-head'>
                                            <FiCheckCircle className='panel-head-icon' />
                                            <span>Simple 4-Step Approval Process</span>
                                        </div>
                                        <div className='installment-process-cards'>
                                            <div className='process-step-card'>
                                                <div className='process-step-badge'>01</div>
                                                <div className='process-step-content'>
                                                    <h4>Pick Down Payment &amp; Tenure</h4>
                                                    <p>
                                                        Choose {availableDownPayments.map((d) => `${d}%`).join(', ')} advance with{' '}
                                                        {allConfiguredDurations.join(', ')} monthly installment{' '}
                                                        {allConfiguredDurations.length === 1 ? 'option' : 'options'} configured for this product.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className='process-step-card'>
                                                <div className='process-step-badge'>02</div>
                                                <div className='process-step-content'>
                                                    <h4>Fast Verification (Within 24 Working Hours)</h4>
                                                    <p>Our verification specialist verifies CNIC, address, and references in Lahore with zero unnecessary hurdles.</p>
                                                </div>
                                            </div>
                                            <div className='process-step-card'>
                                                <div className='process-step-badge'>03</div>
                                                <div className='process-step-content'>
                                                    <h4>Two Friendly Guarantors</h4>
                                                    <p>1 family member/relative + 1 local neighborhood shopkeeper or business reference near your residence.</p>
                                                </div>
                                            </div>
                                            <div className='process-step-card'>
                                                <div className='process-step-badge'>04</div>
                                                <div className='process-step-content'>
                                                    <h4>Insured Doorstep Delivery</h4>
                                                    <p>Safely delivered to your home with warranty card, serial verification, and trained handling.</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className='installment-accordion-wrap'>
                                            <button
                                                type='button'
                                                className='installment-accordion-toggle'
                                                onClick={() => setShowTermsAccordion(!showTermsAccordion)}
                                                aria-expanded={showTermsAccordion}
                                            >
                                                <span>Detailed Leasing Agreement &amp; Policies (11 Clauses)</span>
                                                {showTermsAccordion ? <FiChevronUp /> : <FiChevronDown />}
                                            </button>
                                            {showTermsAccordion && (
                                                <div className='installment-accordion-body'>
                                                    <ol className='installment-agreement-list'>
                                                        <li><strong>Eligibility:</strong> Products valued at PKR 15,000 or above are eligible.</li>
                                                        <li><strong>Application Fee:</strong> Non-refundable processing fee of PKR 1,000.</li>
                                                        <li><strong>Verification:</strong> Takes 2 to 3 working days via phone and address check.</li>
                                                        <li><strong>Guarantor Requirement:</strong> Two guarantors required (1 family relative + 1 local reference).</li>
                                                        <li><strong>Cheque Requirement:</strong> Orders exceeding PKR 100,000 may require a security cheque.</li>
                                                        <li><strong>Installment Plans:</strong> Home appliances up to 15 months; Bikes up to 20 months.</li>
                                                        <li><strong>Payment Terms:</strong> Monthly installments must be paid on agreed date.</li>
                                                        <li><strong>Ownership:</strong> Remains property of Rahber Corporation until full payment.</li>
                                                        <li><strong>Delivery:</strong> Dispatched promptly after verification approval.</li>
                                                        <li><strong>Jurisdiction:</strong> Laws of Pakistan, Lahore jurisdiction apply.</li>
                                                    </ol>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tab 3: Warranty & Authenticity Guarantee */}
                        {activeTab === 'warranty' && (
                            <div className='tab-warranty-pane'>
                                <div className='warranty-hero-box'>
                                    <div className='warranty-badge-emblem'>
                                        <FiShield />
                                    </div>
                                    <div className='warranty-hero-text'>
                                        <h3>Original Box-Packed Products</h3>
                                        <p>
                                            Every appliance from Rahber Corporation is supplied in original factory packing with manufacturer warranty card included. Warranty coverage can be claimed at official brand customer service centers across Pakistan.
                                        </p>
                                    </div>
                                </div>

                                <div className='warranty-points-grid'>
                                    <div className='warranty-point-card'>
                                        <div className='point-number'>01</div>
                                        <h4>Official Warranty Card Inside</h4>
                                        <p>Includes serial-stamped warranty certificate with compressor and parts coverage under brand policy.</p>
                                    </div>
                                    <div className='warranty-point-card'>
                                        <div className='point-number'>02</div>
                                        <h4>Nationwide Service Support</h4>
                                        <p>Claim warranty repairs at authorized brand service centers in Lahore and across major cities.</p>
                                    </div>
                                    <div className='warranty-point-card'>
                                        <div className='point-number'>03</div>
                                        <h4>Showroom Assistance on Arrival</h4>
                                        <p>Inspect physical condition and packaging upon collection or delivery with complete guidance.</p>
                                    </div>
                                    <div className='warranty-point-card'>
                                        <div className='point-number'>04</div>
                                        <h4>Direct Helpline Support</h4>
                                        <p>Our Lahore showroom staff helps coordinate manufacturer customer service details if needed.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tab 4: Showroom Pickup & Location */}
                        {activeTab === 'showroom' && (
                            <div className='tab-showroom-pane'>
                                <div className='showroom-card-layout'>
                                    <div className='showroom-visual-card'>
                                        <img
                                            src={REAL_SHOP_IMAGES.storefront}
                                            alt='Rahber Electronics Showroom Lahore'
                                            className='showroom-hero-img'
                                        />
                                        <div className='showroom-chip-overlay'>
                                            <FiMapPin /> Flagship Lahore Store
                                        </div>
                                    </div>

                                    <div className='showroom-details-card'>
                                        <h3>Visit or Self-Pickup at Lahore Showroom</h3>
                                        <p>
                                            You are always welcome to inspect <strong>{product.name}</strong> in person before purchasing, consult with our installment specialists, or pick up your order directly.
                                        </p>

                                        <div className='showroom-info-list'>
                                            <div className='showroom-info-row'>
                                                <FiMapPin className='info-row-icon' />
                                                <div>
                                                    <strong>Address:</strong>
                                                    <span>{BUSINESS_ADDRESS_SINGLE}</span>
                                                </div>
                                            </div>
                                            <div className='showroom-info-row'>
                                                <FiPhoneCall className='info-row-icon' />
                                                <div>
                                                    <strong>Phone &amp; WhatsApp:</strong>
                                                    <span>
                                                        {BUSINESS_PHONE_MAIN.map((p, i) => (
                                                            <span key={p.tel}>
                                                                <a href={`tel:${p.tel}`} style={{ color: 'inherit', textDecoration: 'none' }}>{p.display}</a>
                                                                {i < BUSINESS_PHONE_MAIN.length - 1 && ' / '}
                                                            </span>
                                                        ))}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='showroom-info-row'>
                                                <FiCheckCircle className='info-row-icon' />
                                                <div>
                                                    <strong>Showroom Timings:</strong>
                                                    <span>{BUSINESS_HOURS.lines[0]} (Mon to Sat)</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className='showroom-actions'>
                                            <a
                                                href={BUSINESS_MAP_URL}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='btn btn-primary btn-map-directions'
                                            >
                                                <FiMapPin /> Open in Google Maps
                                            </a>
                                            <a
                                                href={`${BUSINESS_WHATSAPP_CHAT_URL}?text=${encodeURIComponent(`Hello Rahber Corporation, I would like to schedule a showroom visit to inspect: ${product.name}.`)}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='btn btn-secondary'
                                            >
                                                <FaWhatsapp /> Reserve for Showroom Pickup
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Related Products Shelf (Similar Verified Appliances) */}
                {relatedProducts.length > 0 && (
                    <section className='product-related-shelf'>
                        <div className='shelf-header'>
                            <div>
                                <span className='section-eyebrow'>RECOMMENDED APPLIANCES</span>
                                <h2 className='shelf-title'>Similar Genuine Electronics</h2>
                            </div>
                            <Link to='/products' className='shelf-all-link'>
                                View All Products &rarr;
                            </Link>
                        </div>
                        <div className='product-related-grid'>
                            {relatedProducts.map((p) => (
                                <ProductCard key={p._id} product={p} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Showroom Map & Directions Section (Instant Trust & In-Person Inspection) */}
                <ShowroomMapSection
                    title="Inspect This Product at Our Lahore Showroom"
                    subtitle={`Visit our flagship store at Main Bazar Chandni Chowk, Sanda Kalan, Lahore to examine ${product.name} in person before purchasing. Live demo, testing, and installment guidance available with zero obligation.`}
                />
            </div>

            {/* Quick One-Click Buy Now Modal */}
            <QuickBuyModal
                isOpen={isQuickBuyOpen}
                onClose={() => setIsQuickBuyOpen(false)}
                product={product}
                cashUnitPrice={cashUnitPrice}
                initialQuantity={quantity}
                purchaseMode={selectedPurchaseMode}
                installmentCalc={installmentCalc}
                selectedPlan={selectedPlanTier}
                onSuccess={handleOrderPlaced}
            />

            {/* Order Confirmation & Anxiety-Free WhatsApp Follow-up Modal */}
            <SuccessModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                title={
                    selectedPurchaseMode === 'cash'
                        ? 'Cash Order Received Successfully!'
                        : selectedPurchaseMode === 'four_month'
                        ? '0% Markup Application Received!'
                        : 'Installment Application Received!'
                }
                message={
                    selectedPurchaseMode === 'cash'
                        ? 'Your cash order has been recorded in our Lahore Showroom dispatch system. Our order specialist will call you shortly to confirm your delivery address and schedule.'
                        : 'Your installment application has been recorded in our showroom verification system. Our verification specialist will review your details and contact you within 24 hours.'
                }
                referenceId={placedOrderSummary?.order_number || placedOrderSummary?.orderNumber || placedOrderSummary?.id}
                phone={placedOrderSummary?.customer_phone || placedOrderSummary?.phone}
                productName={placedOrderSummary?.items?.[0]?.name || placedOrderSummary?.productName || product?.name}
                orderTotal={
                    placedOrderSummary?.total_amount !== undefined
                        ? formatPriceDisplay(placedOrderSummary.total_amount)
                        : (placedOrderSummary?.totalAmount !== undefined
                            ? formatPriceDisplay(placedOrderSummary.totalAmount)
                            : undefined)
                }
                isOrder={true}
                order={placedOrderSummary}
            />
        </div>
    );
};

export default ProductDetailPage;
